import React, { useEffect, useState } from "react";
import { Alert, Button, CircularProgress, MenuItem, TextField, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import DevopsRecordShell from "../components/devops/DevopsRecordShell";
import { devopsService } from "../api/services/devopsService";
import { useAuth } from "../contexts/AuthContext";
import { useDevopsWorkspace } from "../contexts/DevopsWorkspaceContext";
import { EntityStatus } from "../types";
import type { ApplicationKind, DeploymentType, DevopsApplication } from "../types/devops";
import { canMaintainApplication, parseCsvList } from "../utils/devopsAccess";
import { devopsRoutes, type DevopsRecordMode } from "../utils/devopsRoutes";
import { getTenantId } from "../utils/jwtUtils";

const APPLICATION_KINDS: ApplicationKind[] = [
  "STANDALONE",
  "MICROSERVICE",
  "UI",
  "CLOUD_FUNCTION",
  "CLOUD_SERVICE",
  "SERVERLESS",
];

const DEPLOYMENT_TYPES: DeploymentType[] = [
  "KUBERNETES",
  "CONTAINER",
  "VM",
  "CLOUD_FUNCTION",
  "SERVERLESS",
  "STATIC",
];

interface ApplicationRecordPageProps {
  mode: DevopsRecordMode;
}

const ApplicationRecordPage: React.FC<ApplicationRecordPageProps> = ({ mode }) => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bindEntity } = useDevopsWorkspace();
  const canWrite = canMaintainApplication(user?.roles ?? []);
  const readOnly = mode === "view";

  const [item, setItem] = useState<DevopsApplication | null>(null);
  const [loading, setLoading] = useState(mode !== "create");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [applicationKind, setApplicationKind] = useState<ApplicationKind>("MICROSERVICE");
  const [deploymentType, setDeploymentType] = useState<DeploymentType>("KUBERNETES");
  const [sourceRepository, setSourceRepository] = useState("");
  const [owner, setOwner] = useState(user?.email ?? "");
  const [managers, setManagers] = useState("");
  const [status, setStatus] = useState<EntityStatus>(EntityStatus.ACTIVE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === "edit" && !canWrite && uuid) {
      navigate(devopsRoutes.applicationView(uuid), { replace: true });
    }
  }, [mode, canWrite, uuid, navigate]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (mode === "create" || !uuid) return;
      setLoading(true);
      setError(null);
      try {
        const app = await devopsService.getApplication(uuid);
        if (cancelled) return;
        if (!app) {
          setError("Application not found.");
          return;
        }
        setItem(app);
        setName(app.name);
        setDescription(app.description ?? "");
        setApplicationKind(app.applicationKind);
        setDeploymentType(app.deploymentType);
        setSourceRepository(app.sourceRepository ?? "");
        setOwner(app.owner);
        setManagers((app.managers ?? []).join(", "));
        setStatus(app.status);
        bindEntity({ type: "APPLICATION", uuid: app.uuid, name: app.name });
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load application");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [mode, uuid, bindEntity]);

  const handleSave = async () => {
    const tenantId = getTenantId();
    if (!tenantId) {
      setError("No tenant on the current session.");
      return;
    }
    if (!name.trim() || !owner.trim()) {
      setError("Name and owner are required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const managersList = parseCsvList(managers);
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        applicationKind,
        deploymentType,
        sourceRepository: sourceRepository.trim() || undefined,
        owner: owner.trim(),
        managers: managersList,
        status,
      };
      const saved = item
        ? await devopsService.updateApplication(item.uuid, payload)
        : await devopsService.createApplication({ tenantId, ...payload });
      navigate(devopsRoutes.applicationView(saved.uuid));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save application");
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === "create" ? "Add application" : mode === "edit" ? `Edit ${item?.name ?? "application"}` : item?.name ?? "Application";

  if (loading) return <CircularProgress size={24} />;

  return (
    <DevopsRecordShell
      title={title}
      backPath={mode === "edit" && uuid ? devopsRoutes.applicationView(uuid) : devopsRoutes.applications}
      backLabel={mode === "edit" ? "Back to view" : "Back to list"}
      actions={
        mode === "view" && canWrite && uuid ? (
          <Button variant="contained" onClick={() => navigate(devopsRoutes.applicationEdit(uuid))}>
            Edit
          </Button>
        ) : undefined
      }
    >
      {error && <Alert severity="error">{error}</Alert>}
      <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required disabled={readOnly} />
      <TextField
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        multiline
        minRows={2}
        disabled={readOnly}
      />
      <TextField
        select
        label="Application kind"
        value={applicationKind}
        onChange={(e) => setApplicationKind(e.target.value as ApplicationKind)}
        disabled={readOnly}
      >
        {APPLICATION_KINDS.map((value) => (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Deployment type"
        value={deploymentType}
        onChange={(e) => setDeploymentType(e.target.value as DeploymentType)}
        disabled={readOnly}
      >
        {DEPLOYMENT_TYPES.map((value) => (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Source repository"
        value={sourceRepository}
        onChange={(e) => setSourceRepository(e.target.value)}
        disabled={readOnly}
      />
      <TextField label="Owner" value={owner} onChange={(e) => setOwner(e.target.value)} required disabled={readOnly} />
      <TextField
        label="Managers"
        value={managers}
        onChange={(e) => setManagers(e.target.value)}
        helperText="Comma-separated emails or user ids"
        disabled={readOnly}
      />
      <TextField
        select
        label="Status"
        value={status}
        onChange={(e) => setStatus(e.target.value as EntityStatus)}
        disabled={readOnly}
      >
        {Object.values(EntityStatus).map((value) => (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        ))}
      </TextField>
      {readOnly && (item?.environments ?? []).length > 0 && (
        <Typography variant="body2">
          Environments:{" "}
          {(item?.environments ?? []).map((env, index) => (
            <React.Fragment key={env.uuid}>
              {index > 0 ? ", " : null}
              <RouterLink to={devopsRoutes.environmentView(env.uuid)}>{env.name}</RouterLink>
            </React.Fragment>
          ))}
        </Typography>
      )}
      {!readOnly && (
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ alignSelf: "flex-start" }}>
          Save
        </Button>
      )}
    </DevopsRecordShell>
  );
};

export default ApplicationRecordPage;
